import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { Fade } from 'react-awesome-reveal';
import { useRouter } from 'next/router';
import { cn } from '../../../utils/cn';
import { PODCAST_URL } from '../../../utils/constants';
import { linkAttributes } from '../../../utils/linkAttributes';
import type { NavContent, NavItemId } from '../../../utils/LangContext';
import MobileNavIcon, { MobileNavIconName } from './MobileNavIcon';
import {
  NavWrapper,
  NavList,
  NavLink,
  BottomNav,
  Social,
  Hoverable,
} from './Nav.styles';

interface NavProps {
  nav: NavContent;
  isOpen: boolean;
  closeNav: () => void;
  locale: string | undefined;
}

const MOBILE_MENU_QUERY = 'not ((width > 768px) and (height > 700px))';
const NAV_TOGGLE_ID = 'NavToggle';

const socialNav: Array<{
  title: string;
  ariaLabel: string;
  link: string;
  icon: Extract<MobileNavIconName, 'linkedin' | 'youtube' | 'instagram'>;
}> = [
  {
    title: 'linkedin',
    ariaLabel: 'LinkedIn',
    link: 'https://www.linkedin.com/company/acueductostudio/',
    icon: 'linkedin',
  },
  {
    title: 'youtube',
    ariaLabel: 'YouTube',
    link: 'https://www.youtube.com/channel/UCuFV_fKt_ELREPwoAb5lprg',
    icon: 'youtube',
  },
  {
    title: 'instagram',
    ariaLabel: 'Instagram',
    link: 'https://www.instagram.com/acueducto.studio/',
    icon: 'instagram',
  },
];

const primaryGridPlacements: Record<NavItemId, string> = {
  home: 'col-start-4 row-start-3',
  about: 'col-start-2 row-start-4',
  work: 'col-start-4 row-start-2',
  services: 'col-start-3 row-start-3',
  contact: 'col-start-3 row-start-4',
};

const primaryRevealDelayByIcon: Record<NavItemId, string> = {
  contact: 'delay-[210ms]',
  services: 'delay-[300ms]',
  home: 'delay-[240ms]',
  about: 'delay-[270ms]',
  work: 'delay-[330ms]',
};

const socialGridPlacements = [
  'col-start-2 row-start-2',
  'col-start-1 row-start-2',
  'col-start-2 row-start-1',
];

const socialRevealDelayByIndex = [
  'delay-[120ms]',
  'delay-[150ms]',
  'delay-[180ms]',
];

const mobileTileSizeClasses =
  'h-[clamp(5.8rem,18.5vmin,7.2rem)] w-[clamp(5.8rem,18.5vmin,7.2rem)]';

const mobileIconSizeClasses =
  'h-[clamp(2.5rem,7.7vmin,3rem)] w-[clamp(2.5rem,7.7vmin,3rem)]';

const MOBILE_SQUIRCLE_PATH =
  'M50 0C90 0 100 10 100 50C100 90 90 100 50 100C10 100 0 90 0 50C0 10 10 0 50 0Z';

interface MobileNavTileProps {
  children: React.ReactNode;
  isActive?: boolean;
  muted?: boolean;
}

const MobileNavTile = ({
  children,
  isActive = false,
  muted = false,
}: MobileNavTileProps) => (
  <span className={cn('relative flex', mobileTileSizeClasses)}>
    <svg
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 h-full w-full overflow-visible transition-[filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
        muted
          ? 'group-active:drop-shadow-[0_0_0.9rem_rgba(77,117,234,0.6)]'
          : 'drop-shadow-[0_0.8rem_1.2rem_rgba(0,0,0,0.24)] group-active:drop-shadow-[0_0_0.9rem_rgba(77,117,234,0.6)]',
      )}
      focusable="false"
      viewBox="0 0 100 100"
    >
      <path
        className={cn(
          'transition-[fill,stroke] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
          muted
            ? 'fill-background-light/20 stroke-card-border'
            : isActive
              ? 'fill-accent'
              : 'fill-accent/20 stroke-accent',
        )}
        d={MOBILE_SQUIRCLE_PATH}
        strokeWidth={muted || !isActive ? 1 : 0}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    <svg
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-[-6px] h-[calc(100%+12px)] w-[calc(100%+12px)] scale-95 overflow-visible text-[#4D75EA] opacity-0 transition-[color,opacity,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
        'group-active:scale-100 group-active:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100',
        muted && 'group-focus-visible:text-foreground-lower',
      )}
      fill="none"
      focusable="false"
      stroke="currentColor"
      viewBox="0 0 100 100"
    >
      <path
        d={MOBILE_SQUIRCLE_PATH}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    <span
      className={cn(
        'relative z-10 flex h-full w-full flex-col items-center justify-center gap-[0.4rem] transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]',
        muted
          ? 'text-foreground-lower'
          : isActive
            ? 'text-foreground'
            : 'text-foreground',
      )}
    >
      {children}
    </span>
  </span>
);

const Nav: React.FC<NavProps> = ({ nav, isOpen, closeNav, locale }) => {
  const router = useRouter();
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { ariaLabel: menuLabel, items, mobileShortcut } = nav;

  useEffect(() => {
    if (isOpen) {
      items.forEach((item) => {
        // next/router's prefetch only handles internal routes; cross-domain
        // URLs (https://acueducto.com/es/...) would 404-prefetch noisily.
        if (!item.link.startsWith('http')) router.prefetch(item.link);
      });
    }
  }, [isOpen, router, items]);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      closeNav();
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router, closeNav]);

  useEffect(() => {
    if (!isOpen) return;

    const trigger = document.getElementById(
      NAV_TOGGLE_ID,
    ) as HTMLElement | null;
    const isMobileMenu = window.matchMedia(MOBILE_MENU_QUERY).matches;
    const activeMenu = isMobileMenu
      ? mobileMenuRef.current
      : desktopMenuRef.current;

    const getFocusableElements = () => {
      const menuLinks = activeMenu
        ? Array.from(activeMenu.querySelectorAll<HTMLElement>('a[href]'))
        : [];
      return trigger ? [...menuLinks, trigger] : menuLinks;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeNav();
        trigger?.focus({ preventScroll: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const currentIndex = focusableElements.indexOf(
        document.activeElement as HTMLElement,
      );

      if (currentIndex === -1) {
        event.preventDefault();
        const fallback = event.shiftKey
          ? focusableElements[focusableElements.length - 1]
          : focusableElements[0];
        fallback.focus();
        return;
      }

      event.preventDefault();
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex =
        (currentIndex + direction + focusableElements.length) %
        focusableElements.length;
      focusableElements[nextIndex].focus();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeNav]);

  return (
    <div id="Nav" aria-hidden={!isOpen} inert={!isOpen}>
      <div
        aria-hidden="true"
        className={cn(
          'bg-background pointer-events-none fixed inset-[18px] z-[49] mx-auto max-w-[1502px] transition-opacity',
          isOpen
            ? 'opacity-100 duration-300 ease-[ease]'
            : 'opacity-0 delay-150 duration-300 ease-[ease]',
        )}
      />
      <NavWrapper
        className="hidden [@media(width>768px)_and_(height>700px)]:grid"
        open={isOpen}
        ref={desktopMenuRef}
      >
        <NavList aria-label={menuLabel}>
          <ul>
            {items.map((item, index) => {
              const isExternal = item.link.startsWith('http');
              const isActive = !isExternal && router.pathname === item.link;
              const attrs = linkAttributes(item.link);
              return (
                <li key={item.id}>
                  <Fade delay={200 + index * 50}>
                    <span>0{index + 1}</span>
                    <Link
                      aria-current={isActive ? 'page' : undefined}
                      href={item.link}
                      as={
                        isExternal ? undefined : item.as ? item.as : item.link
                      }
                      locale={isExternal ? undefined : locale}
                      onClick={closeNav}
                      {...attrs}
                    >
                      <NavLink $active={isActive}>{item.title}</NavLink>
                    </Link>
                  </Fade>
                </li>
              );
            })}
          </ul>
        </NavList>
        <BottomNav>
          <Social>
            {socialNav.map((item) => (
              <Hoverable
                aria-label={item.ariaLabel}
                href={item.link}
                key={item.icon}
                onClick={closeNav}
                rel="noopener noreferrer"
                target="_blank"
              >
                {item.title}
                <span>{item.title}</span>
              </Hoverable>
            ))}
          </Social>
        </BottomNav>
      </NavWrapper>

      <div
        className={cn(
          'fixed inset-[18px] z-[49] mx-auto hidden max-w-[1500px] overflow-hidden rounded-[3rem]',
          'mobile-or-landscape:flex',
          isOpen
            ? 'pointer-events-auto'
            : 'pointer-events-none',
        )}
        ref={mobileMenuRef}
      >
        <nav
          aria-label={menuLabel}
          className="relative z-10 mx-auto h-full w-full max-w-[calc(1500px-36px)] short-viewport:max-w-full overflow-x-hidden overflow-y-auto"
        >
          <div className="relative h-full min-h-[29.6rem] min-w-[26.4rem] [--palette-col:clamp(7rem,21.5vmin,9.05rem)] [--palette-row:clamp(7rem,21.5vmin,9.05rem)]">
            <ul className="absolute right-[calc(5rem-(var(--palette-col)/2))] short-viewport:right-[calc(4.8rem-(var(--palette-col)/2))] bottom-[calc(5.2rem-(var(--palette-row)/2))] grid grid-cols-[repeat(4,var(--palette-col))] grid-rows-[repeat(4,var(--palette-row))] place-items-center">
              {items.map((item) => {
                const isExternal = item.link.startsWith('http');
                const isActive = !isExternal && router.pathname === item.link;
                const attrs = linkAttributes(item.link);
                const iconName = item.id;

                return (
                  <li
                    className={cn(
                      'flex h-full w-full items-center justify-center transition-opacity [will-change:opacity] motion-reduce:delay-0 motion-reduce:duration-100',
                      isOpen
                        ? cn(
                            'opacity-100 duration-300 ease-[ease]',
                            primaryRevealDelayByIcon[iconName],
                          )
                        : 'opacity-0 delay-0 duration-150 ease-[ease]',
                      primaryGridPlacements[iconName],
                    )}
                    key={`mobile-item-${item.id}`}
                  >
                    <Link
                      aria-current={isActive ? 'page' : undefined}
                      className="group relative flex w-full touch-manipulation items-center justify-center text-center no-underline [-webkit-tap-highlight-color:transparent] focus-visible:outline-none"
                      href={item.link}
                      as={
                        isExternal ? undefined : item.as ? item.as : item.link
                      }
                      locale={isExternal ? undefined : locale}
                      onClick={closeNav}
                      {...attrs}
                    >
                      <MobileNavTile isActive={isActive}>
                        <MobileNavIcon
                          className={mobileIconSizeClasses}
                          name={iconName}
                        />
                        <span className="w-full max-w-full truncate px-[0.4rem] text-[1rem] leading-none">
                          {item.title}
                        </span>
                      </MobileNavTile>
                    </Link>
                  </li>
                );
              })}

              {mobileShortcut && (
                <li
                  className={cn(
                    'col-start-2 row-start-3 flex h-full w-full items-center justify-center transition-opacity [will-change:opacity] motion-reduce:delay-0 motion-reduce:duration-100',
                    isOpen
                      ? 'opacity-100 delay-[360ms] duration-300 ease-[ease]'
                      : 'opacity-0 delay-0 duration-150 ease-[ease]',
                  )}
                >
                  <Link
                    aria-label={mobileShortcut.ariaLabel}
                    className="group relative flex w-full touch-manipulation items-center justify-center text-center no-underline [-webkit-tap-highlight-color:transparent] focus-visible:outline-none"
                    href={PODCAST_URL}
                    onClick={closeNav}
                    {...linkAttributes(PODCAST_URL)}
                  >
                    <MobileNavTile>
                      <MobileNavIcon
                        className={mobileIconSizeClasses}
                        name={mobileShortcut.id}
                      />
                      <span className="w-full max-w-full truncate px-[0.4rem] text-[1rem] leading-none">
                        {mobileShortcut.title}
                      </span>
                    </MobileNavTile>
                  </Link>
                </li>
              )}

              {socialNav.map((item, index) => (
                <li
                  className={cn(
                    'height-landscape:translate-x-[calc(var(--palette-col)*-1)] height-landscape:translate-y-[var(--palette-row)] flex h-full w-full items-center justify-center transition-opacity [will-change:opacity] motion-reduce:delay-0 motion-reduce:duration-100',
                    isOpen
                      ? cn(
                          'opacity-100 duration-300 ease-[ease]',
                          socialRevealDelayByIndex[index],
                        )
                      : 'opacity-0 delay-0 duration-150 ease-[ease]',
                    socialGridPlacements[index],
                  )}
                  key={`mobile-social-${item.icon}`}
                >
                  <a
                    aria-label={item.ariaLabel}
                    className="group relative flex touch-manipulation items-center justify-center text-center no-underline [-webkit-tap-highlight-color:transparent] focus-visible:outline-none"
                    href={item.link}
                    onClick={closeNav}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <MobileNavTile muted>
                      <MobileNavIcon
                        className={mobileIconSizeClasses}
                        name={item.icon}
                      />
                    </MobileNavTile>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default React.memo(Nav);
